import React, { useState } from "react";
import { T, Badge } from "./atoms";

// — Warning, LockedOut, Deleted, or anything the engine adds later —
// renders VERBATIM. Never invent a label for a value nobody has
// decided on: an ugly raw word is better than a wrong friendly one,
// the same discipline as the unrecognised-SKU part-number fallback.
export const LICENSE_STATUS_LABELS = { Suspended: "Disabled" };

export const licenseStatusLabel = (capabilityStatus) =>
  LICENSE_STATUS_LABELS[capabilityStatus] || capabilityStatus;

export const licenseBarPct = (assigned, enabled) => {
  const a = Number(assigned), e = Number(enabled);
  if (!Number.isFinite(a) || !Number.isFinite(e) || e <= 0) return 0;
  return Math.max(0, Math.min(100, (a / e) * 100));
};

export const sortLicenseProducts = (products, key, dir) => {
  const mul = dir === "asc" ? 1 : -1;
  const val = (p) => {
    if (key === "product") return (p.product_display_name || p.sku_part_number || "").toLowerCase();
    // Sort on the DISPLAYED label, not the raw value — users sort what
    // they can see, so a pill reading "Disabled" files under D, never
    // under S for the engine's "Suspended".
    if (key === "status") return (licenseStatusLabel(p.capability_status) || "").toLowerCase();
    if (key === "available") return Number(p.available_units) || 0;
    return Number(p.assigned_units) || 0; // "assigned", the default
  };
  return [...(products || [])].filter(Boolean).sort((a, b) => {
    const va = val(a), vb = val(b);
    if (va < vb) return -1 * mul;
    if (va > vb) return 1 * mul;
    return 0;
  });
};

// The supplied model has no data-fetching or action side effects.
export function LicenseTable({ data, cspPanel = null, participatingSkus }) {
  const [sort, setSort] = useState({ key: "assigned", dir: "desc" });
  const sortBy = (key) => {
    setSort(s => s.key === key
      ? { key, dir: s.dir === "desc" ? "asc" : "desc" }
      : { key, dir: (key === "product" || key === "status") ? "asc" : "desc" });
  };
        const products = sortLicenseProducts(data.products, sort.key, sort.dir);
        const activeSkus = participatingSkus || new Set();
        // Frontend owns staleness labels (#506 design): rescan_needed
        // reads "Run a new scan" for a customer — the remedial action in
        // THEIR vocabulary, on their Company Profile.
        const stalePill = data.staleness === "fresh" ? { type:"green", label:"Fresh" }
          : data.staleness === "aging" ? { type:"yellow", label:"Aging" }
          : data.staleness === "rescan_needed" ? { type:"red", label:"Run a new scan" }
          : null;
        const arrow = (key) => sort.key === key ? (sort.dir === "desc" ? " ↓" : " ↑") : "";
        const TH = ({ colKey, align, children }) => (
          <th style={{ textAlign: align || "left", padding:"6px 10px", fontSize:11, color:T.muted, textTransform:"uppercase", letterSpacing:".04em", whiteSpace:"nowrap" }}>
            <button
              onClick={() => sortBy(colKey)}
              style={{ background:"none", border:"none", color:"inherit", font:"inherit", cursor:"pointer", padding:0, textTransform:"inherit", letterSpacing:"inherit" }}
            >{children}{arrow(colKey)}</button>
          </th>
        );
        return (
          <div>
            {cspPanel}
            {/* Customer caption: the collected date ONLY — Source and
                invalid-record counts are Patriot-side facts, and the
                projection doesn't even ship them. */}
            <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap", marginBottom:10, fontSize:12, color:T.muted }}>
              {stalePill && <Badge type={stalePill.type}>{stalePill.label}</Badge>}
              <span>Collected {data.collected_utc ? String(data.collected_utc).slice(0, 10) : "—"}</span>
            </div>
            {/* ONE neutral line, exact approved wording. It must not call
                the missing items products, licences or seats, and must
                not state a number: what InvalidRecordCount counts is
                UNCONFIRMED with the engine owner, and the projection
                ships only a boolean precisely so this copy cannot drift
                into claiming a unit we cannot stand behind. */}
            {data.collection_incomplete === true && (
              <div style={{ fontSize:12, color:T.muted, marginBottom:10 }}>
                Some license records could not be read when this was collected.
              </div>
            )}
            <div style={{ overflowX:"auto" }}>
              <table style={{ borderCollapse:"collapse", width:"100%", minWidth:560 }}>
                <thead>
                  <tr style={{ borderBottom:`1px solid ${T.border}` }}>
                    <TH colKey="product">Product</TH>
                    <TH colKey="available" align="right">Available</TH>
                    <TH colKey="assigned">Assigned</TH>
                    <TH colKey="status">Status</TH>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p, i) => {
                    const pct = licenseBarPct(p.assigned_units, p.enabled_units);
                    return (
                      <tr key={`${p.sku_part_number || "row"}-${i}`} style={{ borderBottom:`1px solid ${T.border}`, background: activeSkus.has(p.sku_part_number) ? "rgba(68,166,255,.08)" : "transparent" }}>
                        <td style={{ padding:"8px 10px", fontSize:13, color:T.text }}>
                          {p.product_display_name || p.sku_part_number || "—"}
                        </td>
                        <td style={{ padding:"8px 10px", fontSize:13, color:T.text, textAlign:"right", fontVariantNumeric:"tabular-nums" }}>
                          {p.available_units ?? "—"}
                        </td>
                        <td style={{ padding:"8px 10px", fontSize:12, color:T.text, minWidth:180 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                            <div style={{ flex:"0 0 90px", height:6, borderRadius:3, background:"rgba(148,163,184,.25)", overflow:"hidden" }}>
                              <div style={{ width:`${pct}%`, height:"100%", borderRadius:3, background:T.blue }} />
                            </div>
                            <span style={{ whiteSpace:"nowrap", fontVariantNumeric:"tabular-nums" }}>
                              {p.assigned_units ?? "—"} / {p.enabled_units ?? "—"}
                            </span>
                          </div>
                        </td>
                        <td style={{ padding:"8px 10px" }}>
                          <Badge type={p.capability_status === "Enabled" ? "green" : p.capability_status === "Suspended" ? "red" : p.capability_status ? "yellow" : "slate"}>
                            {licenseStatusLabel(p.capability_status) || "—"}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );

}
